import { DATE_INPUT_MIN, DATE_INPUT_MAX, parseLocalDate } from '@/lib/dates';
import { addDays, getWeekSunday, localYmd, RANGE_PRESETS, type RangeWeeks } from './gridDates';

/**
 * Jump the crew grid to a date or a date range (#588).
 *
 * The ask was a date control "similar to" the Concrete Pour Schedule's, for
 * quicker navigation. The two grids are not the same shape, and the difference
 * decides what the control does:
 *
 * - The pour schedule holds every pour card it will ever show and FILTERS them
 *   down to the days in range (qaqc/pourRange.ts).
 * - This grid holds one WINDOW — `?start=` (a Sunday) and `?weeks=` (a preset)
 *   — and fetches exactly that window from the API. There is nothing outside
 *   it to filter.
 *
 * So here a From/To MOVES the window rather than hiding columns, and a date
 * far outside what is on screen simply fetches a new window, the same way the
 * ‹ › buttons always have.
 *
 * Pulled out of the page so the rules below are assertable — the component
 * can't be driven in a test (apps/web runs vitest in a node environment with
 * no DOM), and these are the parts that would break silently.
 */

/** What the URL holds: the window's first Sunday and how many weeks it spans. */
export interface GridWindow {
  /** YYYY-MM-DD, always a Sunday. */
  start: string;
  weeks: RangeWeeks;
}

export interface JumpResult extends GridWindow {
  /**
   * The range asked for is longer than the longest preset, so the window
   * shows its FIRST 13 weeks and stops. Surfaced on screen — a range that came
   * back shorter than asked with nothing saying so would read as a bug.
   */
  capped: boolean;
}

/** The longest window the grid will show (13 weeks — a quarter). */
export const MAX_WEEKS: RangeWeeks = RANGE_PRESETS[RANGE_PRESETS.length - 1];

/**
 * A YYYY-MM-DD as a Date at LOCAL midnight, or null if it isn't a real date.
 *
 * Local midnight via `parseLocalDate`, never `new Date('YYYY-MM-DD')`: that
 * form parses as midnight UTC, which is the previous evening anywhere west of
 * Greenwich, and getWeekSunday would then snap a Sunday back to the week
 * before. Same class of bug as #477.
 *
 * Stricter than `parseLocalDate` in two ways a date INPUT needs: the whole
 * string must be the date (no time suffix), and an impossible day (Feb 30,
 * which rolls over to Mar 2) is rejected rather than jumped to.
 */
export function parseYmd(ymd: string): Date | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return null;
  const date = parseLocalDate(ymd);
  return date && localYmd(date) === ymd ? date : null;
}

/**
 * Is this a date the grid should move to?
 *
 * ⚠️ The bounds are not decoration. A date input fires `change` on every
 * keystroke that leaves it holding a complete date, and typing a year digit by
 * digit passes through 0002, 0020 and 0202 on the way to 2026. On the pour
 * schedule that is harmless — it re-filters cards already in memory. Here every
 * change is a fetch, so without this the grid would request the schedule for
 * the year 2 on the way to the date the reader meant.
 */
export function isJumpableYmd(ymd: string): boolean {
  return parseYmd(ymd) !== null && ymd >= DATE_INPUT_MIN && ymd <= DATE_INPUT_MAX;
}

/** The Sunday (YYYY-MM-DD) of the week a date falls in — what the server buckets by. */
export function weekStartOf(ymd: string): string {
  return localYmd(getWeekSunday(parseYmd(ymd)!));
}

/**
 * Sunday-anchored weeks from the week of `from` through the week of `to`,
 * inclusive. Both in the same week is 1.
 */
export function weeksSpanned(from: string, to: string): number {
  const a = getWeekSunday(parseYmd(from)!);
  const b = getWeekSunday(parseYmd(to)!);
  // Rounded, not floored: a span crossing a DST change is an hour short of a
  // whole number of days.
  return Math.round((b.getTime() - a.getTime()) / (7 * 86_400_000)) + 1;
}

/** The smallest preset that shows `n` weeks, or the longest if none does. */
export function presetCovering(n: number): { weeks: RangeWeeks; capped: boolean } {
  const fit = RANGE_PRESETS.find((w) => w >= n);
  return fit ? { weeks: fit, capped: false } : { weeks: MAX_WEEKS, capped: true };
}

/**
 * Where the grid should go for a From/To, given how many weeks it shows now.
 * Either side may be '' (open). Returns null when there is nowhere to go —
 * both sides empty, or a side that isn't a usable date yet (see isJumpableYmd).
 *
 * - **From only** — a jump: the window starts on From's week and keeps its
 *   current length. This is "go to date".
 * - **To only** — the window ENDS on To's week, keeping its length: "the run-up
 *   to" a date.
 * - **Both** — the window starts on From's week and grows to the smallest
 *   preset that reaches To's week. Presets only, because `?weeks=` only
 *   accepts a preset and the column model switches from daily to weekly-peak
 *   at a preset boundary; an arbitrary length would be a third mode. Beyond
 *   13 weeks it shows the first 13 and says so (`capped`).
 *
 * A To before From is treated as From only rather than swapped. The reader is
 * usually halfway through retyping the range, and swapping would throw the
 * grid somewhere neither field says.
 */
export function windowFor(from: string, to: string, currentWeeks: RangeWeeks): JumpResult | null {
  const hasFrom = from !== '';
  const hasTo = to !== '';
  if ((hasFrom && !isJumpableYmd(from)) || (hasTo && !isJumpableYmd(to))) return null;

  if (hasFrom && (!hasTo || to < from)) {
    return { start: weekStartOf(from), weeks: currentWeeks, capped: false };
  }
  if (!hasFrom && hasTo) {
    const lastSunday = getWeekSunday(parseYmd(to)!);
    return { start: localYmd(addDays(lastSunday, -7 * (currentWeeks - 1))), weeks: currentWeeks, capped: false };
  }
  if (hasFrom && hasTo) {
    const { weeks, capped } = presetCovering(weeksSpanned(from, to));
    return { start: weekStartOf(from), weeks, capped };
  }
  return null;
}

/** The window's last day (a Saturday), YYYY-MM-DD. */
export function windowEnd(win: GridWindow): string {
  return localYmd(addDays(parseYmd(win.start)!, win.weeks * 7 - 1));
}
